/**
 * Event bus port — abstraction over platform-specific host-event dispatch.
 *
 * Web: DOM CustomEvents on `window` (existing integrations listen for these)
 * React Native: callback props / in-memory listeners
 */
/** Web implementation — dispatches/listens via DOM CustomEvents on `window`. */
export function createDomEventBus() {
    const listeners = new Map();
    return {
        emit(event, payload) {
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent(event, { detail: payload }));
            }
        },
        on(event, handler) {
            if (typeof window === "undefined")
                return () => { };
            const wrapped = ((e) => handler(e.detail));
            let set = listeners.get(event);
            if (!set) {
                set = new Set();
                listeners.set(event, set);
            }
            set.add(wrapped);
            window.addEventListener(event, wrapped);
            return () => {
                window.removeEventListener(event, wrapped);
                listeners.get(event)?.delete(wrapped);
            };
        },
        off(event) {
            const remove = (name, handlers) => {
                handlers.forEach((h) => window.removeEventListener(name, h));
            };
            if (event) {
                const handlers = listeners.get(event);
                if (handlers)
                    remove(event, handlers);
                listeners.delete(event);
            }
            else {
                listeners.forEach((handlers, name) => remove(name, handlers));
                listeners.clear();
            }
        },
    };
}
/** In-memory event bus — for React Native or testing. */
export function createCallbackEventBus() {
    const listeners = new Map();
    return {
        emit(event, payload) {
            listeners.get(event)?.forEach((handler) => handler(payload));
        },
        on(event, handler) {
            let set = listeners.get(event);
            if (!set) {
                set = new Set();
                listeners.set(event, set);
            }
            const typed = handler;
            set.add(typed);
            return () => {
                listeners.get(event)?.delete(typed);
            };
        },
        off(event) {
            if (event) {
                listeners.delete(event);
            }
            else {
                listeners.clear();
            }
        },
    };
}
